import { Link, usePage } from '@inertiajs/react'
import {
  ChevronDown,
  ClipboardList,
  Clock3,
  CreditCard,
  FileText,
  ListPlus,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBasePath } from '@/lib/paths'
import { cn } from '@/lib/utils'

type ActiveKey =
  | 'timeline'
  | 'documents'
  | 'details'
  | 'additional-information'
  | 'billing'

type SidebarItem = {
  key: ActiveKey
  label: string
  icon: LucideIcon
  href: string
}

// The component Inertia is rendering is the reliable signal for "where am I" —
// each page maps to exactly one rail item, and it never drifts from the URL.
const COMPONENT_TO_ACTIVE: Record<string, ActiveKey> = {
  'Businesses/Show': 'timeline',
  'Businesses/Details': 'details',
  'Businesses/AdditionalInformation': 'additional-information',
}

type BusinessSidebarProps = {
  businessId: string
  activeSection?: 'timeline' | 'documents' | 'billing'
  needsYouCount?: number
}

type MenuGroup = { label: string; items: SidebarItem[] }

/**
 * The business's navigation, defined once.
 *
 * The rail renders it at lg and up; below that the same groups open from a
 * single button in the page header. Two copies of a menu is two places to forget
 * to add the next item.
 */
function useBusinessMenu(
  businessId: string,
  activeSection?: BusinessSidebarProps['activeSection'],
  needsYouCount?: number,
) {
  const basePath = useBasePath()
  const { component } = usePage()
  const active = activeSection ?? COMPONENT_TO_ACTIVE[component]

  const groups: MenuGroup[] = [
    {
      label: 'This company',
      items: [
        {
          key: 'timeline',
          label: needsYouCount ? `Timeline (${needsYouCount})` : 'Timeline',
          icon: Clock3,
          href: `${basePath}/businesses/${businessId}`,
        },
        { key: 'documents', label: 'Documents', icon: FileText, href: `${basePath}/businesses/${businessId}/documents` },
        { key: 'details', label: 'Details', icon: ClipboardList, href: `${basePath}/businesses/${businessId}/details` },
        {
          key: 'additional-information',
          label: 'Additional information',
          icon: ListPlus,
          href: `${basePath}/businesses/${businessId}/additional_information`,
        },
        { key: 'billing', label: 'Billing', icon: CreditCard, href: `${basePath}/businesses/${businessId}/billing` },
      ],
    },
  ]

  return { groups, active }
}

export function BusinessSidebar({ businessId, activeSection, needsYouCount }: BusinessSidebarProps) {
  const { groups, active } = useBusinessMenu(businessId, activeSection, needsYouCount)

  return (
    <nav aria-label="Business" className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="mb-1.5 text-sm font-semibold text-foreground">{group.label}</p>
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const isActive = item.key === active
              const rowClass = cn(
                'flex h-8 w-full items-center justify-start gap-2.5 rounded-md px-2 text-left text-sm transition-colors',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive
                  ? 'bg-muted font-medium text-foreground'
                  : 'font-normal text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )

              return (
                <li key={item.key}>
                  <Link href={item.href} aria-current={isActive ? 'page' : undefined} className={rowClass}>
                    <item.icon className="size-4 shrink-0" aria-hidden="true" />
                    {item.label}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

/**
 * The same menu, for screens too narrow for the rail.
 *
 * A row of buttons in the page header only worked while there were four
 * destinations; there are eleven now across three groups, and a wrapped grid of
 * them pushes the actual content off the screen. One button that says where you
 * are and opens the rest is the shape that scales.
 */
export function BusinessMenuButton({
  businessId,
  activeSection,
  needsYouCount,
  className,
}: BusinessSidebarProps & { className?: string }) {
  const { groups, active } = useBusinessMenu(businessId, activeSection, needsYouCount)
  const current = groups.flatMap((g) => g.items).find((i) => i.key === active)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={cn('h-9 gap-1.5', className)}>
          {current?.label ?? 'Menu'}
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {groups.map((group, i) => (
          <DropdownMenuGroup key={group.label}>
            {i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-muted-foreground">{group.label}</DropdownMenuLabel>
            {group.items.map((item) => {
              const isActive = item.key === active
              return (
                <DropdownMenuItem
                  key={item.key}
                  asChild
                  className={cn(isActive && 'bg-muted text-foreground')}
                >
                  <Link href={item.href} aria-current={isActive ? 'page' : undefined}>
                    <item.icon className="size-4" aria-hidden="true" />
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
